/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import * as React from 'react';
import {client, providers} from '@logilab/cwclientlibjs';
import {TranslationContext} from './commons';

/**
 * The basic information required for getting the entities related to a specific one
 */
export interface EntityRelationsSpecification {
    /**
     * The entity
     */
    entity: providers.Entity;
    /**
     * The shema for the relations in the group
     */
    relations: providers.EntityRelationSchema[];
    /**
     * Whether the entity is the relation's subject
     */
    entityIsSubject: boolean;
    /**
     * The RQL client to use
     */
    rqlClient: client.RqlClient;
    /**
     * The provider of entities
     */
    entityProvider: providers.EntityProvider;
    /**
     * The provider of entity schemas
     */
    schemaProvider: providers.EntitySchemaProvider;
}

/**
 * Changes to entity relations
 */
export interface EntityRelationsChanges {
    /**
     * The EIDs of the entities added to the relation
     */
    added: providers.Entity[];
    /**
     * The EIDs of the entities removed from the relation
     */
    removed: providers.Entity[];
}

/**
 * The parameters for editors of relations
 */
export interface EntityRelationsEditorProps {
    /**
     * The specification for the related entities
     */
    specification: EntityRelationsSpecification;
    /**
     * The base changes to the relations to be included as reference during the edition
     */
    baseChanges: EntityRelationsChanges;
    /**
     * The CSS class name(s) for the component
     */
    className: string;
    /**
     * Validates changes made through the editor (and close it)
     */
    validate: (changes: EntityRelationsChanges) => void;
    /**
     * Cancels any changes and close the editor
     */
    cancel: () => void;
    /**
     * Renders the component used to lookup candidate relatable entities
     * @param component The current editor component
     */
    renderLookup: (component: EntityRelationsEditor) => JSX.Element;
    /**
     * Renders a specific relatable candidate entity
     * @param component The current editor component
     * @param entity The candidate entity
     * @param status The status of the entity in the relation
     */
    renderRelatable: (
        component: EntityRelationsEditor,
        entity: providers.Entity,
        status: RelationStatus
    ) => JSX.Element[];
    /**
     * Renders the content of the editors with the candidate relatable entities
     */
    renderContentRelatables: (component: EntityRelationsEditor) => JSX.Element;
    /**
     * Renders the content of the editor when loading
     * @param component The current editor component
     */
    renderContentLoading: (component: EntityRelationsEditor) => JSX.Element;
    /**
     * Renders the validation button for the editor
     * @param component The current editor component
     */
    renderButtonValidate: (component: EntityRelationsEditor) => JSX.Element;
    /**
     * Renders the cancel button for the editor
     * @param component The current editor component
     */
    renderButtonCancel: (component: EntityRelationsEditor) => JSX.Element;
    /**
     * Renders the component
     * @param component The current editor component
     */
    render: (component: EntityRelationsEditor) => JSX.Element;
}

/**
 * The state for editors of relations
 */
export interface EntityRelationsEditorState {
    /**
     * The base count of related entities,
     * i.e. the number of entities in the relations given by the specification,
     * modified by the base changes not made in this editor
     */
    baseCount: number;
    /**
     * The current changes made by this editor
     */
    changes: EntityRelationsChanges;
    /**
     * Whether candidate relatables entities are currently loading
     */
    isLoadingRelatables: boolean;
    /**
     * The current relatables results
     */
    relatables: providers.Entity[];
}

/**
 * The common interface of editors of relations
 */
export class EntityRelationsEditor<
    P extends EntityRelationsEditorProps = EntityRelationsEditorProps,
    S extends EntityRelationsEditorState = EntityRelationsEditorState
> extends React.Component<P, S> {
    public static contextType = TranslationContext;
    public context!: React.ContextType<typeof TranslationContext>;
    /**
     * Whether the component is currently mounted
     */
    _isMounted: boolean;
    /**
     * Handles event when the selection is currently loading the entities
     */
    onLookupLoading: () => void;
    /**
     * Handles event when relatable candidates have been found
     * @param result The result of the lookup
     */
    onLookupResult: (result: providers.Entity[]) => void;
    /**
     * Handles the event for adding/removing the entity from the relation
     * @param entity The related entity
     * @param isRelated Whether the entity is now related to the reference one
     */
    onToggle: (entity: providers.Entity, isRelated: boolean) => void;
}

/**
 * The relation status of an entity
 */
export enum RelationStatus {
    /**
     * Not in the reference relation
     */
    Unrelated,
    /**
     * In the reference relation
     */
    Related,
    /**
     * Added to the relation by the current changes
     */
    Added,
    /**
     * Removed from the relation by the current changes
     */
    Removed,
}

/**
 * Determines the status of a candidate entity for the relation of interest
 * @param specification The base specification for related entities
 * @param baseChanges The base changes made outside the current editor
 * @param currentChanges The changes made by the current editor
 * @param candidate The candidate relatable entity
 */
export function getEntityRelationStatus(
    specification: EntityRelationsSpecification,
    baseChanges: EntityRelationsChanges,
    currentChanges: EntityRelationsChanges,
    candidate: providers.Entity
): RelationStatus {
    if (
        currentChanges.added.find((e) => e['@eid'] === candidate['@eid']) !==
        undefined
    ) {
        return RelationStatus.Added;
    }
    if (
        currentChanges.removed.find((e) => e['@eid'] === candidate['@eid']) !==
        undefined
    ) {
        return RelationStatus.Removed;
    }
    if (
        baseChanges.added.find((e) => e['@eid'] === candidate['@eid']) !==
        undefined
    ) {
        return RelationStatus.Related;
    }
    if (
        baseChanges.removed.find((e) => e['@eid'] === candidate['@eid']) !==
        undefined
    ) {
        return RelationStatus.Unrelated;
    }
    for (let i = 0; i !== specification.relations.length; i++) {
        let relation = specification.relations[i];
        let relationName = providers.getUniqueRelationName(relation);
        if (
            specification.entity[relationName].indexOf(candidate['@eid']) >= 0
        ) {
            return RelationStatus.Related;
        }
    }
    return RelationStatus.Unrelated;
}

/**
 * Gets the current number of entities related to the reference one according to the specification
 * @param specification The specification
 */
export function getRelatedCount(
    specification: EntityRelationsSpecification
): Promise<number> {
    if (specification.entityIsSubject) {
        let count = 0;
        for (let i = 0; i !== specification.relations.length; i++) {
            let relation = specification.relations[i];
            let relationName = providers.getUniqueRelationName(relation);
            let values = specification.entity[relationName];
            if (values !== undefined && values !== null) {
                count += (values as number[]).length;
            }
        }
        return new Promise((resolve, rejects) => resolve(count));
    } else {
        return specification.rqlClient
            .queryRows(
                `Any Y WHERE Y ${specification.relations[0].name} X, X eid %(x)s`,
                {x: specification.entity['@eid']}
            )
            .then((rows) => {
                return rows.length;
            });
    }
}
